import { useMemo, type RefObject } from 'react';
import type { ShootScriptRow } from '../../../shared/scriptRows';
import { AUDIO_TYPE_META, type AudioType } from '../../../shared/audioTypes';
import { VISUAL_TYPE_META, type VisualType } from '../../../shared/visualTypes';
import { AUDIO_TYPE_ICONS, VISUAL_TYPE_ICONS } from '../Editor/CellTypeChip';
import { ShootStrip } from './ShootTabs';
import {
  SCRIPT_FILTERS,
  applyScriptFilter,
  filterCounts,
  useScriptFilter,
  type ScriptFilter,
} from './useScriptFilter';

/**
 * The Script Shoot View (mock-up C3): every script row as a reading line, the
 * audio large enough to read off a phone at arm's length and the visuals
 * underneath for the camera. A filter narrows the list to the lines spoken to
 * camera or to the voiceover. Rows arrive already stripped of empties.
 */

const FILTER_LABELS: Record<ScriptFilter, string> = {
  all: 'All',
  to_camera: 'To camera',
  voiceover: 'VO',
};

interface Props {
  slug: string;
  rows: ShootScriptRow[];
  /** Same parking rules as the tab strip. Both left out when the filters sit
   *  inside a tab, under a strip that already sticks. */
  stickyUnder?: RefObject<HTMLElement | null>;
  stickyAtTop?: boolean;
}

interface SectionGroup {
  section: string | null;
  rows: ShootScriptRow[];
}

/** Consecutive rows under one heading, so a campaign's variants read as blocks. */
function groupBySection(rows: ShootScriptRow[]): SectionGroup[] {
  const groups: SectionGroup[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.section === row.section) last.rows.push(row);
    else groups.push({ section: row.section, rows: [row] });
  }
  return groups;
}

export default function ShootScriptTab({ slug, rows, stickyUnder, stickyAtTop }: Props) {
  const counts = useMemo(() => filterCounts(rows), [rows]);
  const [filter, setFilter] = useScriptFilter(slug, counts);
  const visible = useMemo(() => applyScriptFilter(rows, filter), [rows, filter]);
  const groups = useMemo(() => groupBySection(visible), [visible]);
  // A filter with no rows under it is hidden; with only All left there is
  // nothing to choose between, so the control goes too.
  const offered = SCRIPT_FILTERS.filter((f) => f === 'all' || counts[f] > 0);

  function choose(next: ScriptFilter) {
    if (next === filter) return;
    setFilter(next);
    window.scrollTo({ top: 0 });
  }

  return (
    <div>
      {offered.length > 1 && (
        // Bled into the page gutters: the pill is narrower than the column,
        // so lines scrolling under it would otherwise show at either side.
        <ShootStrip stickyUnder={stickyUnder} stickyAtTop={stickyAtTop} className="-mx-5 px-5">
          <div
            role="group"
            aria-label="Show lines"
            className="inline-flex gap-0.5 p-0.5 rounded-full bg-elevated border border-line"
          >
            {offered.map((f) => {
              const on = f === filter;
              return (
                <button
                  key={f}
                  type="button"
                  aria-pressed={on}
                  onClick={() => choose(f)}
                  className={`
                    rounded-full px-3.5 py-1.5 font-mono text-[12px] font-semibold tracking-[0.02em]
                    whitespace-nowrap transition-colors duration-150
                    ${on ? 'bg-card text-fg shadow-flare/30' : 'text-faint hover:text-muted'}
                  `}
                >
                  {FILTER_LABELS[f]}{' '}
                  <span className={on ? 'text-muted' : 'text-faint'}>· {counts[f]}</span>
                </button>
              );
            })}
          </div>
        </ShootStrip>
      )}

      <div className={offered.length > 1 ? 'mt-2' : undefined}>
        {groups.map((group, i) => (
          <section key={`${i}-${group.section ?? ''}`} aria-label={group.section ?? undefined}>
            {group.section && (
              <h2
                className={`font-mono text-[11px] uppercase tracking-[0.14em] text-accent pb-2 ${
                  i === 0 ? 'pt-2' : 'pt-8'
                }`}
              >
                {group.section}
              </h2>
            )}
            <ol>
              {group.rows.map((row) => (
                <ScriptLine key={row.number} row={row} />
              ))}
            </ol>
          </section>
        ))}
      </div>

      <p className="text-faint text-[12px] mt-12 text-center">
        {filter === 'all'
          ? `${rows.length} row${rows.length === 1 ? '' : 's'}`
          : `${visible.length} of ${rows.length} rows`}
      </p>
    </div>
  );
}

function ScriptLine({ row }: { row: ShootScriptRow }) {
  const type = row.audioType;
  const hasAudio = row.audio.length > 0;
  return (
    <li
      className="shoot-line py-5 border-t border-line"
      data-audio-type={type ?? undefined}
    >
      <div className="flex items-center gap-2.5 mb-2.5">
        {type && <AudioChip type={type} />}
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-faint">
          Row {row.number}
        </span>
      </div>
      {hasAudio && <LineAudio type={type} paragraphs={row.audio} />}
      {row.visuals.length > 0 && <LineVisuals paragraphs={row.visuals} visualType={row.visualType} lead={!hasAudio} />}
    </li>
  );
}

/** The editor's read-only chip, same palette and icon (see CellTypeChip.ts). */
function AudioChip({ type }: { type: AudioType }) {
  return (
    <span className="cell-type-chip cell-type-chip--readonly">
      <span
        className="cell-type-chip__ic"
        aria-hidden
        dangerouslySetInnerHTML={{ __html: AUDIO_TYPE_ICONS[type] }}
      />
      <span>{AUDIO_TYPE_META[type].label}</span>
    </span>
  );
}

function VisualChip({ type }: { type: VisualType }) {
  return (
    <span className="cell-type-chip cell-type-chip--readonly mr-2 align-middle" data-visual-type={type}>
      <span
        className="cell-type-chip__ic"
        aria-hidden
        dangerouslySetInnerHTML={{ __html: VISUAL_TYPE_ICONS[type] }}
      />
      <span>{VISUAL_TYPE_META[type].label}</span>
    </span>
  );
}

const OPEN_QUOTE = /^["“'‘]/;
const CLOSE_QUOTE = /["”'’]$/;

/**
 * To-camera lines are said out loud by the person on screen, so they read as
 * a quote. On-screen text is not spoken at all and reads as a caption.
 */
function LineAudio({ type, paragraphs }: { type: AudioType | null; paragraphs: string[] }) {
  if (type === 'silent') {
    return (
      <div className="space-y-1.5 text-[16px] md:text-[17px] leading-[1.5] text-muted whitespace-pre-line">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    );
  }
  const quoted = type === 'to_camera';
  const last = paragraphs.length - 1;
  return (
    <div
      className={`space-y-2 text-[19px] md:text-[22px] leading-[1.45] text-fg whitespace-pre-line ${
        quoted ? 'font-medium' : ''
      }`}
    >
      {paragraphs.map((p, i) => (
        <p key={i}>
          {quoted && i === 0 && !OPEN_QUOTE.test(p) && '“'}
          {p}
          {quoted && i === last && !CLOSE_QUOTE.test(p) && '”'}
        </p>
      ))}
    </div>
  );
}

/** A row with no audio leads with its visuals, so they step up a size. */
function LineVisuals({ paragraphs, visualType, lead }: {
  paragraphs: string[];
  visualType: VisualType | null;
  lead: boolean;
}) {
  return (
    <div
      className={`${lead ? 'text-[16px] md:text-[17px]' : 'mt-2.5 text-[14px] md:text-[15px]'} leading-[1.5] text-muted whitespace-pre-line`}
    >
      {paragraphs.map((p, i) => (
        <p key={i} className={i > 0 ? 'mt-1' : undefined}>
          {i === 0 && (
            visualType ? <VisualChip type={visualType} /> : (
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint mr-2">
                Visuals
              </span>
            )
          )}
          {p}
        </p>
      ))}
    </div>
  );
}
